"use client";

import { useEffect, useState, type RefObject } from "react";
import type { InsightMessage } from "../context/AnalyzeFileContext";

const STICKY_HEADER_HEIGHT_PX = 44;

/** Locked to filter when set; otherwise follows the topmost visible insight in All view. */
export function useFeedStickyPersona(
  scrollRef: RefObject<HTMLElement | null>,
  messages: InsightMessage[],
  feedFilter: string | null,
) {
  const [scrolledPersonaId, setScrolledPersonaId] = useState<string | null>(
    feedFilter ?? messages[0]?.personaId ?? null,
  );

  useEffect(() => {
    if (feedFilter) {
      setScrolledPersonaId(feedFilter);
      return;
    }

    const root = scrollRef.current;
    if (!root) {
      setScrolledPersonaId(messages[0]?.personaId ?? null);
      return;
    }

    const update = () => {
      if (messages.length === 0) {
        setScrolledPersonaId(null);
        return;
      }

      const stickyBottom = root.getBoundingClientRect().top + STICKY_HEADER_HEIGHT_PX;
      let activeId = messages[0].personaId;
      let closestTop = -Infinity;

      for (const msg of messages) {
        const el = document.getElementById(`analyze-insight-${msg.id}`);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= stickyBottom && top > closestTop) {
          closestTop = top;
          activeId = msg.personaId;
        }
      }

      setScrolledPersonaId(activeId);
    };

    update();
    root.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(root);

    return () => {
      root.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      ro?.disconnect();
    };
  }, [feedFilter, messages, scrollRef]);

  return feedFilter ?? scrolledPersonaId;
}
